import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AppSettings } from '../lib/api.js';
import { toast } from '../lib/toast-store.js';
import { QueryError } from '../components/query-error.js';

/**
 * Admin → Settings. Operational, admin-configurable values the API actually
 * consumes (order create reads the UDF keys + type codes + A/R-hold guard +
 * freeze; the cart flags margins below the floor). Server enforces admin on save.
 */
export function SettingsScreen() {
  const settings = useQuery({ queryKey: ['settings'], queryFn: ({ signal }) => api.settings.get(signal) });

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Admin · Settings</h2>
          <div className="sub">Order policy & Spire field mappings</div>
        </div>
        <Link to="/me" className="back-btn">‹ Me</Link>
      </div>
      <div style={{ paddingTop: 8 }}>
        <HealthPanel />
        {settings.isPending && <div className="kd-skeleton" style={{ height: 200 }} />}
        {settings.isError && <QueryError message={settings.error.message} onRetry={() => void settings.refetch()} />}
        {settings.data && <SettingsForm initial={settings.data} />}
      </div>
    </>
  );
}

/**
 * One-glance system health: is it us, the tunnel, or their Spire box?
 * Auto-refreshes every 30s while the page is open.
 */
function HealthPanel() {
  const health = useQuery({
    queryKey: ['settings-health'],
    queryFn: ({ signal }) => api.settings.health(signal),
    refetchInterval: 30_000,
  });
  const h = health.data;

  return (
    <>
      <div className="section-title">System health</div>
      <div className="kd-card">
        {health.isPending && <div className="kd-skeleton" style={{ height: 60 }} />}
        {health.isError && (
          <div className="kd-muted" style={{ fontSize: 13 }}>
            Health check unavailable: {health.error.message}
          </div>
        )}
        {h && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '8px 16px', fontSize: 13 }}>
            <HealthRow
              label={`Spire (${h.spire.host} · ${h.spire.company})`}
              ok={h.spire.ok}
              detail={h.spire.ok ? `${h.spire.latencyMs} ms` : `unreachable (HTTP ${h.spire.httpStatus ?? '—'})`}
            />
            <HealthRow label="Database" ok={h.db.ok} detail={h.db.ok ? `${h.db.latencyMs} ms` : 'unreachable'} />
            <HealthRow
              label="Spire account"
              ok={!h.spire.authHold}
              detail={
                h.spire.authHold
                  ? `rejected — ${h.spire.authHold.reason} (next check ${new Date(h.spire.authHold.until).toLocaleTimeString()})`
                  : 'accepted'
              }
            />
            <HealthRow
              label="Circuit breaker"
              ok={h.spire.breaker !== 'open'}
              warn={h.spire.breaker === 'half_open'}
              detail={h.spire.breaker.replace('_', '-')}
            />
            <HealthRow
              label="Order placement"
              ok={!h.app.ordersReadonly}
              warn={h.app.ordersReadonly}
              detail={h.app.ordersReadonly ? 'read-only (pre-UAT)' : 'enabled'}
            />
            <div className="kd-muted" style={{ gridColumn: '1 / -1', fontSize: 11.5 }}>
              {h.app.version} · {h.app.nodeEnv} · up {formatUptime(h.app.uptimeSec)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function HealthRow({ label, ok, warn, detail }: { label: string; ok: boolean; warn?: boolean; detail: string }) {
  const color = warn ? '#a8770a' : ok ? 'var(--kd-green-dark)' : 'var(--kd-danger)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span aria-hidden style={{ width: 9, height: 9, borderRadius: 999, background: color, flexShrink: 0 }} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <span className="kd-muted" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>{detail}</span>
    </div>
  );
}

function formatUptime(sec: number): string {
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  return `${Math.floor(sec / 86400)}d ${Math.floor((sec % 86400) / 3600)}h`;
}

function SettingsForm({ initial }: { initial: AppSettings }) {
  const qc = useQueryClient();
  const [s, setS] = useState<AppSettings>(initial);
  const set = <K extends keyof AppSettings>(k: K, v: AppSettings[K]) => setS((p) => ({ ...p, [k]: v }));
  const dirty = JSON.stringify(s) !== JSON.stringify(initial);

  const save = useMutation({
    mutationFn: () => api.settings.update(s),
    onSuccess: () => {
      toast('Settings saved');
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Save failed', 'error'),
  });

  return (
    <>
      <div className="section-title">Order guardrails</div>
      <div className="kd-card">
        <div className="admin-toggles">
          <Toggle label="Freeze ordering" on={s.ordersFrozen} onChange={(v) => set('ordersFrozen', v)} />
          <Toggle label="Block A/R-hold accounts" on={s.enforceArHold} onChange={(v) => set('enforceArHold', v)} />
        </div>
        <label className="admin-field" style={{ marginTop: 12 }}>
          <span>Below-floor behavior</span>
          <select value={s.minMarginEnforce} onChange={(e) => set('minMarginEnforce', e.target.value)}>
            <option value="warn">Warn only (badge on the line)</option>
            <option value="block">Block checkout (price-override users exempt)</option>
          </select>
        </label>
        <label className="admin-field" style={{ marginTop: 12 }}>
          <span>Minimum margin % (floor)</span>
          <input
            type="number"
            min={0}
            max={100}
            value={s.minMarginPct}
            onChange={(e) => set('minMarginPct', Math.max(0, Math.min(100, Math.round(Number(e.target.value)) || 0)))}
          />
        </label>
        {s.ordersFrozen && (
          <div className="kd-muted" style={{ fontSize: 11.5, marginTop: 8 }}>
            ⚠ Ordering is frozen — checkout and order edits are blocked for everyone.
          </div>
        )}
      </div>

      <div className="section-title">Spire field mappings</div>
      <div className="kd-card">
        <div className="admin-grid">
          <label className="admin-field">
            <span>Order type code</span>
            <input value={s.orderTypeCode} maxLength={4} onChange={(e) => set('orderTypeCode', e.target.value)} />
          </label>
          <label className="admin-field">
            <span>Quote type code</span>
            <input value={s.quoteTypeCode} maxLength={4} onChange={(e) => set('quoteTypeCode', e.target.value)} />
          </label>
          <label className="admin-field">
            <span>Rep-audit UDF key</span>
            <input value={s.repAuditUdfKey} maxLength={40} onChange={(e) => set('repAuditUdfKey', e.target.value)} />
          </label>
          <label className="admin-field">
            <span>Order-notes UDF key</span>
            <input value={s.orderNotesUdfKey} maxLength={40} onChange={(e) => set('orderNotesUdfKey', e.target.value)} />
          </label>
        </div>
        <div className="kd-muted" style={{ fontSize: 11.5, marginTop: 8 }}>
          These map to K-Dental's Spire fields. Confirm against their live Spire before go-live.
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary"
        style={{ margin: '4px 14px 16px', width: 'calc(100% - 28px)' }}
        disabled={!dirty || save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : dirty ? 'Save settings' : 'Saved'}
      </button>
    </>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button type="button" className={`admin-toggle${on ? ' on' : ''}`} aria-pressed={on} onClick={() => onChange(!on)}>
      <span className="dot" />
      {label}
    </button>
  );
}
